'use client';

import { useEffect } from 'react';
import Script from 'next/script';
import { usePPC } from 'components/PPCProvider';
import { useAuth } from 'components/Auth/AuthProvider';

type Props = {
  currentPage: string;
};

function getCurrentOS(): string {
  const userAgent = window.navigator.userAgent;
  const platform =
    (window.navigator as any)?.userAgentData?.platform ||
    window.navigator.platform;

  const macosPlatforms = ['macOS', 'Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
  const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
  const iosPlatforms = ['iPhone', 'iPad', 'iPod'];

  if (macosPlatforms.includes(platform)) return 'Mac OS';
  if (iosPlatforms.includes(platform)) return 'iOS';
  if (windowsPlatforms.includes(platform)) return 'Windows';
  if (/Android/.test(userAgent)) return 'Android';
  if (/Linux/.test(platform)) return 'Linux';

  return 'Not detected';
}

function detectIsMobile(): string {
  const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
  const isMobile =
    /android|iphone|ipad|ipod|mobile|opera mini|iemobile|blackberry/i.test(
      ua.toLowerCase()
    );
  return isMobile ? 'true' : 'false';
}

export default function SurvicateTracker({ currentPage }: Props) {
  const { isPPC } = usePPC();
  const { isLogged, me } = useAuth();

  useEffect(() => {
    const currentOs = getCurrentOS();
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const browserLanguage = navigator.language;
    const isMobile = detectIsMobile();

    const traits = {
      email: me?.email || '',
      first_name: me?.fullName || '',
      last_name: '',
      current_page: currentPage,
      time_zone: timeZone,
      operating_system: currentOs,
      device_width: screen.width,
      device_height: screen.height,
      browser_language: browserLanguage,
      is_mobile: isMobile,
      is_ppc: String(isPPC),
    };

    (window as any)._sva = (window as any)._sva || {};
    (window as any)._sva.traits = traits;

    if ((window as any)._sva?.setVisitorTraits) {
      (window as any)._sva.setVisitorTraits(traits);
    }
  }, [currentPage, isPPC, me]);

  const handleScriptLoad = () => {
    if ((window as any)._sva?.setVisitorTraits && me?.email) {
      const traits = {
        email: me.email,
        first_name: me.fullName || '',
        last_name: '',
        current_page: currentPage,
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        operating_system: getCurrentOS(),
        device_width: screen.width,
        device_height: screen.height,
        browser_language: navigator.language,
        is_mobile: detectIsMobile(),
        is_ppc: String(isPPC),
      };
      (window as any)._sva.setVisitorTraits(traits);
    }
  };

  return (
    <Script
      src="https://survey.survicate.com/workspaces/9af079a99bd3425ecbf8c904eb9ce521/web_surveys.js"
      strategy="afterInteractive"
      onLoad={handleScriptLoad}
    />
  );
}
